import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Cloud, Loader2, Mail, ShieldCheck } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { z } from "zod";

import { ApiError, authApi, setupApi, type SetupStatus } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { describePasswordPolicy } from "../lib/passwordPolicy";

const searchSchema = z.object({ email: z.string().optional() });

export const Route = createFileRoute("/setup")({
  validateSearch: (search) => searchSchema.parse(search),
  component: SetupPage,
});

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

function SetupPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const setMe = useAuthStore((state) => state.setMe);
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [configured, setConfigured] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const [setupToken, setSetupToken] = useState("");
  const [orgName, setOrgName] = useState("Biz360");
  const [smtpHost, setSmtpHost] = useState("smtp.gmail.com");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpFrom, setSmtpFrom] = useState("");
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleClientSecret, setGoogleClientSecret] = useState("");

  const [ownerEmail, setOwnerEmail] = useState(search.email ?? "");
  const [ownerName, setOwnerName] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    setupApi
      .status()
      .then((result) => {
        setStatus(result);
        setConfigured(result.smtp_configured);
      })
      .catch((cause) => setError(errorMessage(cause, "Could not check setup status.")))
      .finally(() => setLoading(false));
  }, []);

  const saveConfiguration = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy("config");
    try {
      const result = await setupApi.configure({
        setup_token: setupToken,
        org_name: orgName,
        smtp_host: smtpHost,
        smtp_port: Number(smtpPort),
        smtp_user: smtpUser,
        smtp_password: smtpPassword,
        smtp_from: smtpFrom,
        test_email: ownerEmail || smtpFrom,
        google_oauth_client_id: googleClientId,
        google_oauth_client_secret: googleClientSecret,
      });
      setConfigured(true);
      setNotice(result.message);
      setStatus(await setupApi.status());
      setSmtpPassword("");
      setGoogleClientSecret("");
    } catch (cause) {
      setError(errorMessage(cause, "Could not save the setup configuration."));
    } finally {
      setBusy(null);
    }
  };

  const sendOwnerCode = async () => {
    setError(null);
    setNotice(null);
    setBusy("code");
    try {
      const result = await setupApi.requestOwnerCode(setupToken, ownerEmail, ownerName);
      setCodeSent(true);
      setNotice(result.message);
    } catch (cause) {
      setError(errorMessage(cause, "Could not send the Owner verification code."));
    } finally {
      setBusy(null);
    }
  };

  const finish = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy("finish");
    try {
      const me = await authApi.verifyOnboarding(ownerEmail, otp, password, ownerName);
      setMe(me);
      navigate({ to: "/" });
    } catch (cause) {
      setError(errorMessage(cause, "Could not complete setup."));
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="grid min-h-screen place-items-center"><Loader2 className="size-6 animate-spin" /></div>;
  }

  if (status && !status.needs_setup) {
    return (
      <div className="grid min-h-screen place-items-center px-4">
        <div className="w-full max-w-md rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-6 text-center shadow-sm">
          <CheckCircle2 className="mx-auto mb-3 size-9 text-emerald-600" />
          <h1 className="text-xl font-semibold">Biz360 is already set up</h1>
          <p className="mt-2 text-sm opacity-65">Use the sign-in page to continue.</p>
          <Link to="/login" search={{}} className="mt-5 inline-flex min-h-11 items-center rounded-md bg-[var(--accent)] px-4 text-sm font-medium text-white">Go to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <main className="mx-auto min-h-screen w-full max-w-5xl px-4 py-8 sm:px-6 lg:py-12">
      <header className="mb-8">
        <p className="text-sm font-medium text-[var(--accent)]">First-time setup</p>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">Set up your Biz360 workspace</h1>
        <p className="mt-2 max-w-2xl text-sm opacity-65">Configure secure email delivery, prepare optional Google access, and verify the first Owner—all in the browser.</p>
      </header>

      {(error || notice) && (
        <div role={error ? "alert" : "status"} className={`mb-6 rounded-lg border px-4 py-3 text-sm ${error ? "border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300" : "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"}`}>
          {error || notice}
        </div>
      )}

      {!status?.setup_token_configured && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          The server has no installation code. The hosting installer must generate <code>SETUP_TOKEN</code> before browser setup can continue.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-6">
          <form onSubmit={saveConfiguration} className="space-y-5 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-sm sm:p-6">
            <div className="flex items-start gap-3"><Mail className="mt-0.5 size-5 text-[var(--accent)]" /><div><h2 className="font-semibold">1. Email and workspace</h2><p className="text-sm opacity-60">A test email must arrive before the Owner can be created.</p></div></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Installation code"><input className="form-input w-full" type="password" required value={setupToken} onChange={(e) => setSetupToken(e.target.value)} autoComplete="off" /></Field>
              <Field label="Workspace name"><input className="form-input w-full" required value={orgName} onChange={(e) => setOrgName(e.target.value)} /></Field>
              <Field label="SMTP host"><input className="form-input w-full" required value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} /></Field>
              <Field label="SMTP port"><input className="form-input w-full" type="number" required value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} /></Field>
              <Field label="SMTP username"><input className="form-input w-full" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} autoComplete="username" /></Field>
              <Field label="SMTP password / app password"><input className="form-input w-full" type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} autoComplete="new-password" placeholder={status?.smtp_configured ? "Leave blank to keep existing" : "Required by most providers"} /></Field>
              <Field label="From email"><input className="form-input w-full" type="email" required value={smtpFrom} onChange={(e) => setSmtpFrom(e.target.value)} /></Field>
              <Field label="Owner email (receives the test)"><input className="form-input w-full" type="email" required value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} /></Field>
            </div>
            <details className="rounded-lg border border-[var(--border)] p-4">
              <summary className="cursor-pointer text-sm font-medium">Optional: save Google OAuth credentials now</summary>
              <div className="mt-4 grid gap-4">
                <Field label="OAuth client ID"><input className="form-input w-full" value={googleClientId} onChange={(e) => setGoogleClientId(e.target.value)} placeholder={status?.google_configured ? "Already configured—leave blank to keep" : "…apps.googleusercontent.com"} /></Field>
                <Field label="OAuth client secret"><input className="form-input w-full" type="password" value={googleClientSecret} onChange={(e) => setGoogleClientSecret(e.target.value)} placeholder={status?.google_configured ? "Already configured—leave blank to keep" : "Google client secret"} /></Field>
              </div>
            </details>
            <button disabled={busy !== null || !status?.setup_token_configured} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[var(--accent)] px-4 text-sm font-medium text-white disabled:opacity-50">
              {busy === "config" && <Loader2 className="size-4 animate-spin" />} Save and send test email
            </button>
          </form>

          <form onSubmit={finish} className="space-y-5 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-sm sm:p-6">
            <div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 size-5 text-[var(--accent)]" /><div><h2 className="font-semibold">2. Verify the first Owner</h2><p className="text-sm opacity-60">The Owner becomes the protected primary administrator.</p></div></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Owner name"><input className="form-input w-full" required value={ownerName} onChange={(e) => setOwnerName(e.target.value)} autoComplete="name" /></Field>
              <Field label="Owner email"><input className="form-input w-full" type="email" required value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} autoComplete="email" /></Field>
            </div>
            <button type="button" onClick={sendOwnerCode} disabled={!configured || busy !== null || !ownerName || !ownerEmail} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[var(--border)] px-4 text-sm font-medium disabled:opacity-50">
              {busy === "code" && <Loader2 className="size-4 animate-spin" />} Email verification code
            </button>
            {codeSent && <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Six-digit code"><input className="form-input w-full font-mono tracking-[0.3em]" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} autoComplete="one-time-code" /></Field>
              <div />
              <Field label="Password"><input className="form-input w-full" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /><span className="mt-1 block text-xs opacity-55">{describePasswordPolicy()}</span></Field>
              <Field label="Confirm password"><input className="form-input w-full" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" /></Field>
            </div>}
            {codeSent && <button disabled={busy !== null} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[var(--accent)] px-4 text-sm font-medium text-white disabled:opacity-50">{busy === "finish" && <Loader2 className="size-4 animate-spin" />} Finish setup</button>}
          </form>
        </div>

        <aside className="h-fit space-y-4 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-sm sm:p-6 lg:sticky lg:top-6">
          <div className="flex items-start gap-3"><Cloud className="mt-0.5 size-5 text-[var(--accent)]" /><div><h2 className="font-semibold">Google setup guide</h2><p className="text-sm opacity-60">Optional. You may finish Owner setup first.</p></div></div>
          <ol className="list-decimal space-y-3 pl-5 text-sm opacity-80">
            <li>Open Google Cloud Console and create a project owned by your organization.</li>
            <li>Enable <strong>Google Drive API</strong>.</li>
            <li>Configure the OAuth consent screen for an external app. Add intended testers while the app remains in Testing.</li>
            <li>Create an OAuth client of type <strong>Web application</strong>.</li>
            <li>Add both exact callback addresses shown below.</li>
            <li>Copy the client ID and secret into this wizard, save, and finish Owner setup.</li>
            <li>After signing in, open Settings → Google Drive and choose the Google account that should own the backup folder.</li>
          </ol>
          <Callback label="Drive callback" value={status?.drive_callback_uri ?? ""} />
          <Callback label="Employee login callback" value={status?.login_callback_uri ?? ""} />
          <div className="rounded-lg border border-[var(--border)] p-3 text-xs opacity-70">Biz360 never asks for the Google-account password. A personal Gmail account works; Google Workspace is optional.</div>
        </aside>
      </div>
    </main>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block text-sm"><span className="mb-1 block opacity-80">{label}</span>{children}</label>;
}

function Callback({ label, value }: { label: string; value: string }) {
  return <div><p className="mb-1 text-xs font-medium uppercase tracking-wide opacity-55">{label}</p><code className="block overflow-x-auto rounded-md bg-[var(--bg)] p-2 text-xs">{value}</code></div>;
}
